import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Field } from '@components/dynamic-input-fields/src/dynamic-input-fields.type';
import { FIELD_EMAIL, FIELD_QUANTITY } from '@components/dynamic-input-fields/src/utils';
import { EventSummaryCard } from '@components/event-summary-card';
import { IconPerson } from '@components/icons';
import { Loader } from '@components/loader';
import { ModalStickyFooter } from '@components/modals/modal-sticky-footer';
import { SendButtonText } from '@components/process-invite-guest/components/send-button-text';
import {
  InviteGuestForm,
  MULTIPLE_INVITE,
  SINGLE_INVITE,
} from '@components/process-invite-guest/src/process-invite-guest.type';
import { useGetEvent } from '@hooks/events/use-get-event';
import { useGetInvitation } from '@hooks/invitation/use-get-invitation';
import { getTranslatedPropertyBasedOnOrder } from '@utils/get-translated-property-based-on-order/get-translated-property-based-on-order';

import { INFO_TYPE_FIELD, InviteGuestFillInfoProps } from './invite-guest.types';
import { MultiInviteGuest } from './multi-invite';
import { InviteGuestSingleInvite } from './single-invite';

export const FillInfo = ({ onContinue, handleClose }: InviteGuestFillInfoProps) => {
  const { t, i18n } = useTranslation();
  const {
    getValues,
    watch,
    formState: { isValid },
  } = useFormContext<InviteGuestForm>();
  const invitationId: string = getValues('invitationId');
  const eventId = getValues('eventId');
  const quantity = getValues(FIELD_QUANTITY);
  const inviteType = getValues('type');
  const watchedSelectedOption = watch(INFO_TYPE_FIELD);
  const watchedEmail = watch(FIELD_EMAIL);

  const { data: invitation } = useGetInvitation({ invitationId });
  const { data: event } = useGetEvent({ eventId });

  // TODO: https://xceeddev.atlassian.net/browse/XCEED-6332 Create send link invite guest process.
  // const options: FillInfoOption[] = useMemo(() => {
  //   const toggleOption = (option: typeof OPTION_FILL_INFO | typeof OPTION_SEND_LINK) => {
  //     if (watchedSelectedOption === option) return [];
  //     setValue(INFO_TYPE_FIELD, option, {
  //       shouldDirty: true,
  //       shouldValidate: true,
  //       shouldTouch: true,
  //     });
  //   };
  //   return [
  //     {
  //       name: t('invitations:fillInfoOption'),
  //       bordered: true,
  //       subtitle: t('invitations:fillInfoSubtitle'),
  //       id: OPTION_FILL_INFO as typeof OPTION_FILL_INFO,
  //       icon: IconPencil,
  //       active: watchedSelectedOption === OPTION_FILL_INFO,
  //       wrapperClasses: 'flex-1',
  //       action: () => toggleOption(OPTION_FILL_INFO),
  //     },
  //     {
  //       name: t('invitations:sendLinkOption'),
  //       bordered: true,
  //       subtitle: t('invitations:sendLinkSubtitle'),
  //       id: OPTION_SEND_LINK as typeof OPTION_SEND_LINK,
  //       icon: IconSend,
  //       active: watchedSelectedOption === OPTION_SEND_LINK,
  //       wrapperClasses: 'flex-1',
  //       action: () => toggleOption(OPTION_SEND_LINK),
  //     },
  //   ];
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [t, watchedSelectedOption]);

  if (!event || !invitation)
    return (
      <div className='relative max-h-[80vh] overflow-y-auto'>
        <Loader />
      </div>
    );

  const invitationName = getTranslatedPropertyBasedOnOrder({
    translations: invitation?.name,
    preferredLanguage: i18n.language,
  });

  const requiredFields = invitation.bookingFormFields.filter(
    (bookingFormField) => bookingFormField.isRequired
  ) as Field[];

  return (
    <div className='relative max-h-[80vh] overflow-y-auto'>
      <header className='flex flex-col px-4 sm:px-20 pt-8 text-left'>
        <div className='flex flex-col gap-2 mb-4'>
          <p className='text-gray-700 text-sm dark:text-gray-500'>{t('invitations:fillInfoTitle')}</p>
          <h2 className='text-2xl font-black line-clamp-2 dark:text-white'>{invitationName}</h2>
          {quantity > 0 && (
            <p className='flex text-sm gap-2 text-gray-700 dark:text-gray-500'>
              <IconPerson className='w-4 h-4' /> {t('invitations:guests', { count: quantity })}
            </p>
          )}
        </div>
        <EventSummaryCard
          locale={i18n.language}
          name={event.name}
          startingTime={event.startingTime}
          coverUrl={event.coverUrl}
          clubName={event.club.name}
          timezone={event.club.city.timezone}
        />
      </header>
      {inviteType === SINGLE_INVITE && (
        <InviteGuestSingleInvite
          options={[]}
          requiredFields={requiredFields}
          isoCode={event.club.city.country.isoCode}
        />
      )}
      {inviteType === MULTIPLE_INVITE && (
        <MultiInviteGuest
          title={t('invitations:inviteListInstructionsLabel')}
          instructionsTitle={t('invitations:inviteListInstructionsTitle')}
          instructionsDescription={t('invitations:inviteListInstructionsText')}
          textareaPlaceholder={t('invitations:inviteListTextareaPlaceholder')}
        />
      )}
      <ModalStickyFooter
        primaryButtonText={
          <span className='flex items-center gap-2'>
            <SendButtonText watchedSelectedOption={watchedSelectedOption} email={watchedEmail} t={t} />
          </span>
        }
        onPrimaryButtonClick={onContinue}
        onSecondaryButtonClick={handleClose}
        secondaryButtonText={t('common:discard')}
        isSubmitDisabled={!isValid}
      />
    </div>
  );
};
